import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableContainer,
  Tooltip,
  Typography,
  TableSortLabel,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useToast } from '../../components/ToastContext';
import {
  getFeedbackByUserIdAPI,
  downloadFileByIdAPI,
} from '../../api/suggestion';
import SuggestionDetailsDialog from './SuggestionDetailsDialog';
import { useConfig } from "../../services/ConfigContext";



const trimFileName = (name, max = 25) =>
  name && name.length > max ? `${name.slice(0, max)}...` : name;

const SuggestionList = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const toast = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [typeFilter, setTypeFilter] = useState('ALL');
  const [anchorEl, setAnchorEl] = useState(null);

  const [orderBy, setOrderBy] = useState('createdOn');
  const [order, setOrder] = useState('desc'); // latest first

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const { config } = useConfig();
  const isAttachment = config?.isAttachment;

 

  const pageTitle = useMemo(() => {
  try {
    const menus = JSON.parse(sessionStorage.getItem('userMenus') || '[]');
    const matchedMenu = menus.find(
      (m) => m.path?.toLowerCase() === '/suggestions'
    );
    return matchedMenu?.label ?? null;
  } catch {
    return null;
  }
}, []);


  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const userId = sessionStorage.getItem('userId');
      const res = await getFeedbackByUserIdAPI(userId);

      const apiRows = res?.data?.data || res?.data || [];
      const normalizedRows = apiRows.map((row) => ({
        ...row,
        feedbackCategory:
          row.feedbackCategory === 'Bug' ? 'Issue' : row.feedbackCategory,
      }));

      setRows(normalizedRows);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const filteredRows =
    typeFilter === 'ALL'
      ? rows
      : rows.filter(
        (r) =>
          (typeFilter === 1 && r.feedbackCategory === 'Suggestion') ||
          (typeFilter === 2 && r.feedbackCategory === 'Issue')
      );

  const sortedRows = [...filteredRows].sort((a, b) => {
    let aValue;
    let bValue;

    switch (orderBy) {
      case 'createdOn':
        aValue = new Date(a.createdOn);
        bValue = new Date(b.createdOn);
        break;

      case 'attachments':
        aValue = a.uploadedFiles?.length || 0;
        bValue = b.uploadedFiles?.length || 0;
        break;

        case 'createdBy':
        aValue = (a.userName || '').toString().toLowerCase();
        bValue = (b.userName || '').toString().toLowerCase();
        break;
        
      default:
        aValue = (a[orderBy] || '').toString().toLowerCase();
        bValue = (b[orderBy] || '').toString().toLowerCase();
    }

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedRows =
    rowsPerPage > 0
      ? sortedRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
      : sortedRows;

  const handleDownload = async (id, name) => {
    try {
      const res = await downloadFileByIdAPI(id);
      let blob = res.data;

      // 1. Declare our filename fallback variable at the top scope
      let finalFileName = name;

      // 2. Detect JSON masquerading as a Blob
      if (blob instanceof Blob && blob.type === 'application/json') {
        const text = await blob.text();
        const jsonResponse = JSON.parse(text);

        // Update the filename if 'name' was empty but the backend provided one
        if (!finalFileName && jsonResponse.fileName) {
          finalFileName = jsonResponse.fileName;
        }

        // 3. Look for the exact "fileContent" key your backend is sending
        if (jsonResponse.fileContent) {
          const base64Data = jsonResponse.fileContent;
          const contentType = jsonResponse.contentType || 'application/octet-stream';

          // 4. Convert Base64 back to a pure binary Blob so Windows can read it
          const byteCharacters = atob(base64Data);
          const byteArrays = [];
          for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
              byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
          }
          // Overwrite the JSON blob with our newly created Image Blob
          blob = new Blob(byteArrays, { type: contentType });

        } else {
          // If it's an actual backend error message (e.g., "File not found")
          console.error("Backend Error:", jsonResponse);
          toast.error(jsonResponse.message || "File could not be found on the server.");
          return;
        }
      } else if (!(blob instanceof Blob)) {
        // Fallback just in case an Axios interceptor stripped the Blob formatting
        blob = new Blob([blob]);
      }

      // 5. Trigger the actual browser download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      // Use the safely scoped variable we declared at the top
      a.download = finalFileName || `attachment-${id}`;

      document.body.appendChild(a);
      a.click();

      // Cleanup
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Download Error:", error);
      toast.error('Download failed. Check console for details.');
    }
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setOpenDialog(true);
  };

  const renderSortableHeader = (label, field) => (
    <TableSortLabel
      active={orderBy === field}
      direction={orderBy === field ? order : 'asc'}
      onClick={() => {
        const isAsc = orderBy === field && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(field);
        setPage(0);
      }}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Box sx={{ p: 2, pt: 0.5, pl: 0 }}>
      <Box display="flex" justifyContent="space-between" mb={1} sx={{ alignItems: 'center' }}>
        <Typography variant="title" sx={{ color: 'text.primary' }}>
        
           {pageTitle && (
  <Typography variant="title" sx={{ m: 0 }}>
    {pageTitle}
     </Typography>
)}

        </Typography>

        <Button
          variant="contained"
          sx={{
            color: isDark ? '#121212' : 'white',
            bgcolor: isDark ? '#90caf9' : '#00aaff',
            '&:hover': {
              bgcolor: isDark ? '#64b5f6' : '#0088cc',
            },
          }}
          onClick={() => navigate('/suggestions/create')}
        >
          Create
        </Button>
      </Box>

      <Paper sx={{ display: "flex", flexDirection: "column", height: 'calc(100vh - 185px)', bgcolor: 'background.paper' }}>

        {loading ? (
          <Box p={4} textAlign="center">
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Scrollable Table */}
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {renderSortableHeader('Title', 'title')}
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box display="flex" alignItems="center">
                        Type
                        <IconButton
                          size="small"
                          onClick={(e) => setAnchorEl(e.currentTarget)}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>

                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                        >
                          <MenuItem
                            sx={{ fontSize: 12 }}
                            onClick={() => {
                              setTypeFilter('ALL');
                              setPage(0);
                              setAnchorEl(null);
                            }}
                          >
                            All
                          </MenuItem>

                          <MenuItem
                            sx={{ fontSize: 12 }}
                            onClick={() => {
                              setTypeFilter(1);
                              setPage(0);
                              setAnchorEl(null);
                            }}
                          >
                            Suggestion
                          </MenuItem>

                          <MenuItem
                            sx={{ fontSize: 12 }}
                            onClick={() => {
                              setTypeFilter(2);
                              setPage(0);
                              setAnchorEl(null);
                            }}
                          >
                            Issue
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      {renderSortableHeader('Status', 'status')}
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      {renderSortableHeader('Description', 'description')}
                    </TableCell>

                    {isAttachment && (
                      <TableCell sx={{ fontWeight: 600 }}>
                        {renderSortableHeader('Attachments', 'attachments')}
                      </TableCell>
                    )}

                    <TableCell sx={{ fontWeight: 600 }}>
                      {renderSortableHeader('Created On', 'createdOn')}
                    </TableCell>

                      <TableCell sx={{ fontWeight: 600 }}>
                      {renderSortableHeader('Created by', 'createdBy')}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedRows.map((row) => (
                    <TableRow
                      key={row.feedbackId}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#f4f4f4",
                        },
                      }}
                      onClick={() => handleRowClick(row)}
                    >
                      <TableCell>{row.title}</TableCell>
                      <TableCell>{row.feedbackCategory}</TableCell>
                      <TableCell>{row.status || "-"}</TableCell>

                      <TableCell>
                        {row.description?.length > 50
                          ? `${row.description.slice(0, 50)}...`
                          : row.description}
                      </TableCell>

                      {isAttachment && (
                        <TableCell>
                          {row.uploadedFiles?.length
                            ? row.uploadedFiles.map((f) => (
                              <Tooltip key={f.id} title={f.fileName}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    cursor: "pointer",
                                    color: isDark ? "#90caf9" : "primary.main",
                                    "&:hover": {
                                      textDecoration: "underline",
                                    },
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(f.id, f.fileName);
                                  }}
                                >
                                  {trimFileName(f.fileName)}
                                </Typography>
                              </Tooltip>
                            ))
                            : "-"}
                        </TableCell>
                      )}

                      <TableCell>
                        {new Date(row.createdOn).toLocaleString()}
                      </TableCell>

                          <TableCell>
                        {row.userName}
                      </TableCell>
                    </TableRow>
                  ))}

                  {paginatedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Sticky Pagination */}
            <TablePagination
              component="div"
              count={sortedRows.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            />
          </>
        )}

      </Paper>
      <SuggestionDetailsDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        data={selectedRow}
        onDownload={handleDownload}
      />
    </Box>
  );
};

export default SuggestionList;
